// src/pages/TripSpaces.jsx
// Staff-facing creator for boarding trip Chat spaces.
// Gated by the `trip_spaces` toolId (SuperAdmin grant) — same pattern as
// Cubby Manager. Creates a trip via the createTripSpace callable, renders the
// join QR in-app, and lists this creator's active trips.
//
// Deps: `qrcode` (npm i qrcode). Uses the app's existing firebase config +
// AuthContext. Adjust the two import paths if your tree differs.

import React, { useEffect, useState, useCallback } from "react";
import QRCode from "qrcode";
import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";

// Default the end date to today+1 (Amman) so the field is never blank.
function defaultEndDate() {
  const now = new Date();
  const amman = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Amman" })
  );
  amman.setDate(amman.getDate() + 1);
  return amman.toISOString().slice(0, 10);
}

export default function TripSpaces() {
  // allowedTools is surfaced on the auth context itself (from the Firestore
  // profile) — NOT on the raw `user`. Read it from the context value.
  const { user, allowedTools = [], isSuperAdmin } = useAuth();

  const [name, setName] = useState("");
  const [endDate, setEndDate] = useState(defaultEndDate());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { joinUrl, spaceUri, name }
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [trips, setTrips] = useState([]);

  // Access gate: superadmin OR trip_spaces in allowedTools.
  const allowed = isSuperAdmin || allowedTools.includes("trip_spaces");

  // Live list of this creator's active trips.
  useEffect(() => {
    if (!user?.uid || !allowed) return;
    const q = query(
      collection(db, "tripSpaces"),
      where("createdBy", "==", user.uid),
      where("status", "==", "active"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setTrips(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("trip list error", e)
    );
    return () => unsub();
  }, [user?.uid, allowed]);

  const renderQr = useCallback(async (url) => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 320,
        margin: 2,
        color: { dark: KA_NAVY, light: "#FFFFFF" },
      });
      setQrDataUrl(dataUrl);
    } catch (e) {
      console.error("QR render failed", e);
      setQrDataUrl("");
    }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setQrDataUrl("");
    if (!name.trim()) {
      setError("Give the trip a name.");
      return;
    }
    setCreating(true);
    try {
      const fns = getFunctions(undefined, "us-central1");
      const createTripSpace = httpsCallable(fns, "createTripSpace");
      const { data } = await createTripSpace({
        name: name.trim(),
        endDate,
      });
      setResult({ joinUrl: data.joinUrl, spaceUri: data.spaceUri, name: name.trim() });
      await renderQr(data.joinUrl);
      setName("");
    } catch (err) {
      setError(err?.message || "Could not create the trip space.");
    } finally {
      setCreating(false);
    }
  };

  const showQrFor = async (trip) => {
    const url = `${window.location.origin}/trip-join?s=${trip.token}`;
    setResult({ joinUrl: url, spaceUri: trip.spaceUri, name: trip.name });
    await renderQr(url);
  };

  const downloadQr = () => {
    if (!qrDataUrl || !result) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `trip-${result.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-qr.png`;
    a.click();
  };

  const printQr = () => {
    if (!qrDataUrl || !result) return;
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>${result.name} — Join QR</title>
      <style>
        body{font-family:system-ui,Segoe UI,Roboto,sans-serif;text-align:center;padding:48px;color:${KA_NAVY}}
        h1{color:${KA_RED};margin-bottom:4px}
        p{color:#555;margin-top:0}
        img{width:360px;height:360px;margin:24px auto;display:block}
        .foot{color:${KA_GOLD};font-size:13px;margin-top:24px}
      </style></head>
      <body>
        <h1>${result.name}</h1>
        <p>Scan with your phone to join the trip chat</p>
        <img src="${qrDataUrl}" alt="Join QR"/>
        <div class="foot">King's Academy · Boarding</div>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  if (!allowed) {
    return (
      <div style={{ padding: 32, maxWidth: 720, margin: "0 auto" }}>
        <h2 style={{ color: KA_NAVY }}>Trip Spaces</h2>
        <p>You don't have access to this tool. Ask a superadmin for the
          <code> trip_spaces </code> grant.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: "0 auto" }}>
      <h2 style={{ color: KA_NAVY, marginBottom: 4 }}>Trip Spaces</h2>
      <p style={{ color: "#666", marginTop: 0 }}>
        Create a private Google Chat space for a boarding trip. Post a message
        and every member is notified in the Chat app — no per-student setup.
      </p>

      <form
        onSubmit={handleCreate}
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-end",
          background: "#fafafa",
          border: "1px solid #eee",
          borderRadius: 12,
          padding: 20,
          marginTop: 16,
        }}
      >
        <label style={{ flex: "1 1 260px", fontSize: 13, color: KA_NAVY }}>
          Trip name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Grade 10 Wadi Rum — Oct 2026"
            style={inputStyle}
          />
        </label>
        <label style={{ fontSize: 13, color: KA_NAVY }}>
          Ends (last day)
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={inputStyle}
          />
        </label>
        <button
          type="submit"
          disabled={creating}
          style={{
            background: creating ? "#aaa" : KA_RED,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "11px 20px",
            fontSize: 14,
            cursor: creating ? "default" : "pointer",
            height: 42,
          }}
        >
          {creating ? "Creating…" : "Create trip space"}
        </button>
      </form>

      {error && (
        <div style={{ color: KA_RED, marginTop: 12, fontSize: 14 }}>{error}</div>
      )}

      {result && (
        <div
          style={{
            marginTop: 24,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            alignItems: "center",
            border: `1px solid ${KA_GOLD}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          {qrDataUrl && (
            <img
              src={qrDataUrl}
              alt="Join QR"
              style={{ width: 220, height: 220 }}
            />
          )}
          <div style={{ flex: "1 1 260px" }}>
            <h3 style={{ margin: "0 0 8px", color: KA_NAVY }}>{result.name}</h3>
            <p style={{ fontSize: 13, color: "#555", margin: "0 0 8px" }}>
              Students scan this to join. They'll be signed in with their KA
              account and added automatically.
            </p>
            <div style={{ fontSize: 12, wordBreak: "break-all", color: "#888" }}>
              {result.joinUrl}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button onClick={downloadQr} style={ghostBtn}>Download PNG</button>
              <button onClick={printQr} style={ghostBtn}>Print</button>
              {result.spaceUri && (
                <a
                  href={result.spaceUri}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...ghostBtn, textDecoration: "none", lineHeight: "20px" }}
                >
                  Open space
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <h3 style={{ color: KA_NAVY, marginTop: 36 }}>Active trips</h3>
      {trips.length === 0 && (
        <p style={{ color: "#888", fontSize: 14 }}>No active trips yet.</p>
      )}
      <div style={{ display: "grid", gap: 10 }}>
        {trips.map((t) => (
          <div
            key={t.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #eee",
              borderRadius: 10,
              padding: "12px 16px",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, color: KA_NAVY }}>{t.name}</div>
              <div style={{ fontSize: 12, color: "#999" }}>
                Ends {t.endDate} · {t.memberCount || 0} joined
              </div>
            </div>
            <button onClick={() => showQrFor(t)} style={ghostBtn}>
              Show QR
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputStyle = {
  display: "block",
  marginTop: 6,
  padding: "10px 12px",
  border: "1px solid #ccc",
  borderRadius: 8,
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
};

const ghostBtn = {
  background: "#fff",
  color: "#233C5F",
  border: "1px solid #233C5F",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: 13,
  cursor: "pointer",
};